import { apiClient } from '@/lib/api'
import { API_CONFIG } from '@/config/api'
import { getAuthToken } from '@/stores/authStore'

export type SupportThreadStatus = 'open' | 'pending' | 'resolved'

export interface SupportThread {
  thread_id: number
  shop_id: number
  customer_id: number
  subject: string | null
  status: SupportThreadStatus
  order_id: number | null
  assigned_staff_id: number | null
  last_message_at: string | null
  last_message_preview: string | null
  created_by_type: 'customer' | 'staff'
  created_by_staff_id: number | null
  created_at: string
  updated_at: string
  resolved_at: string | null
  customer_name?: string | null
  customer_email?: string | null
  customer_code?: string | null
  assignee_name?: string | null
  order_code?: string | null
  unread_count?: number
  linked_staff_id?: number | null
  linked_tenant_shop_id?: number | null
  linked_tenant_shop_name?: string | null
  linked_tenant_shop_code?: string | null
  last_sender_type?: 'customer' | 'staff' | 'system' | null
  is_platform_ticket?: boolean
}

export interface SupportMessageAttachment {
  attachment_id: number
  message_id: number
  thread_id: number
  shop_id: number
  file_name: string
  file_path: string
  file_size: number
  mime_type: string
  uploaded_at: string
}

export interface SupportMessage {
  message_id: number
  thread_id: number
  shop_id: number
  sender_type: 'customer' | 'staff' | 'system'
  sender_staff_id: number | null
  sender_customer_id: number | null
  body: string
  is_internal: boolean
  created_at: string
  sender_name?: string | null
  attachments?: SupportMessageAttachment[]
}

export interface SupportThreadListResult {
  threads: SupportThread[]
  total: number
  page: number
  limit: number
}

export interface SupportUnreadSummary {
  unread_threads: number
  unread_messages: number
}

export interface SupportChatEvent {
  type: 'thread.created' | 'thread.updated' | 'message.created' | 'connected'
  shop_id?: number
  thread_id?: number
  payload?: unknown
}

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export const supportChatService = {
  async listThreads(params: {
    shop_id: number
    status?: SupportThreadStatus | 'all'
    customer_id?: number
    assigned_to_me?: boolean
    q?: string
    page?: number
    limit?: number
    /** Hide resolved threads older than this many days (Resolved / All tabs). */
    hide_resolved_older_than_days?: number
  }) {
    return apiClient.get<{ success: boolean; data: SupportThreadListResult }>(
      `/support/threads${qs(params)}`,
    )
  },

  async getThread(threadId: number) {
    return apiClient.get<{ success: boolean; data: SupportThread }>(`/support/threads/${threadId}`)
  },

  async createThread(body: {
    shop_id?: number
    customer_id?: number
    subject?: string
    body: string
    order_id?: number
    is_internal?: boolean
  }) {
    return apiClient.post<{
      success: boolean
      data: { thread: SupportThread; message: SupportMessage }
    }>('/support/threads', body)
  },

  async listMessages(threadId: number, params?: { after_message_id?: number; limit?: number }) {
    return apiClient.get<{ success: boolean; data: SupportMessage[] }>(
      `/support/threads/${threadId}/messages${qs(params || {})}`,
    )
  },

  async postMessage(
    threadId: number,
    body: { body: string; is_internal?: boolean; files?: File[] },
  ) {
    if (body.files?.length) {
      const form = new FormData()
      form.append('body', body.body || '')
      if (body.is_internal) form.append('is_internal', 'true')
      for (const file of body.files) {
        form.append('files', file)
      }
      return apiClient.post<{ success: boolean; data: SupportMessage }>(
        `/support/threads/${threadId}/messages`,
        form,
      )
    }
    return apiClient.post<{ success: boolean; data: SupportMessage }>(
      `/support/threads/${threadId}/messages`,
      { body: body.body, is_internal: body.is_internal },
    )
  },

  async updateThread(
    threadId: number,
    body: {
      status?: SupportThreadStatus
      assigned_staff_id?: number | null
      subject?: string | null
    },
  ) {
    return apiClient.patch<{ success: boolean; data: SupportThread }>(
      `/support/threads/${threadId}`,
      body,
    )
  },

  async markRead(threadId: number, lastReadMessageId?: number) {
    return apiClient.post<{ success: boolean }>(`/support/threads/${threadId}/read`, {
      last_read_message_id: lastReadMessageId,
    })
  },

  async getUnread(shopId: number) {
    return apiClient.get<{ success: boolean; data: SupportUnreadSummary }>(
      `/support/unread${qs({ shop_id: shopId })}`,
    )
  },

  /**
   * Open an SSE stream. Uses cookie auth when same-origin; falls back to token query if cross-origin.
   * Returns a cleanup function.
   */
  openEventStream(opts: {
    shopId?: number
    threadId?: number
    onEvent: (event: SupportChatEvent) => void
    onError?: () => void
  }): () => void {
    const params = qs({
      shop_id: opts.shopId,
      thread_id: opts.threadId,
    })
    const base = API_CONFIG.BASE_URL.replace(/\/$/, '')
    let url = `${base}/support/events${params}`
    // EventSource cannot set Authorization. Prefer HttpOnly cookies on same-origin;
    // only put the JWT in the query string when the API is cross-origin.
    let crossOrigin = true
    try {
      crossOrigin =
        new URL(API_CONFIG.BASE_URL, window.location.origin).origin !== window.location.origin
    } catch {
      crossOrigin = true
    }
    if (crossOrigin) {
      const token = getAuthToken() || localStorage.getItem('auth-token')
      if (token) {
        url += (params ? '&' : '?') + `access_token=${encodeURIComponent(token)}`
      }
    }

    const es = new EventSource(url, { withCredentials: true })
    const types = ['connected', 'thread.created', 'thread.updated', 'message.created'] as const
    for (const type of types) {
      es.addEventListener(type, (e) => {
        try {
          const data = JSON.parse((e as MessageEvent).data)
          opts.onEvent(type === 'connected' ? { type: 'connected' } : data)
        } catch {
          /* ignore */
        }
      })
    }
    es.onerror = () => {
      opts.onError?.()
    }
    return () => es.close()
  },
}

export default supportChatService
